import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useFormProtection } from '@/hooks/useFormProtection';
import { CharacterCounter } from '@/components/shared/CharacterCounter';
import { sanitizeText, emailSchema, nameSchema, organizationSchema, messageSchema, subjectSchema } from '@/lib/validation';
import { Mail, Send, Lightbulb } from 'lucide-react';
import { PARTNER_CATEGORIES } from '@/lib/admin/constants';

type InquiryType = 'general' | 'support' | 'partner' | 'feature';

const inquiryOptions = [
  { value: 'general', label: 'General Question', description: 'Ask us anything about AutomotiveAPI' },
  { value: 'support', label: 'Technical Support', description: 'Get help with implementation or issues' },
  { value: 'partner', label: 'Partner Request', description: 'Become a founding or contributing partner' },
  { value: 'feature', label: 'Feature Request', description: 'Suggest improvements or new features' },
];

const urgencyLevels = [
  { value: 'low', label: 'Low - General inquiry' },
  { value: 'medium', label: 'Medium - Affects workflow' },
  { value: 'high', label: 'High - Blocking issue' },
];

// Zod schema for contact form
const contactSchema = z.object({
  inquiryType: z.enum(['general', 'support', 'partner', 'feature'], {
    required_error: 'Please select an inquiry type',
  }),
  name: nameSchema,
  email: emailSchema,
  organization: organizationSchema,
  subject: subjectSchema,
  partnerType: z.string().optional(),
  urgency: z.enum(['low', 'medium', 'high']).optional(),
  message: messageSchema,
}).superRefine((data, ctx) => {
  // Type-specific required field validation
  if (data.inquiryType === 'support') {
    if (!data.subject?.trim()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Subject is required', path: ['subject'] });
    }
    if (!data.urgency) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Urgency is required', path: ['urgency'] });
    }
  }
  if (data.inquiryType === 'partner') {
    if (!data.organization?.trim()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Organization is required', path: ['organization'] });
    }
    if (!data.partnerType) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Partner type is required', path: ['partnerType'] });
    }
  }
  if (data.inquiryType === 'feature' && !data.subject?.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Feature title is required', path: ['subject'] });
  }
});

type ContactFormData = z.infer<typeof contactSchema>;

export default function Contact() {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { honeypotProps, validateSubmission, recordSubmission, isRateLimited } = useFormProtection('contact');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isValid },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    mode: 'onChange',
    defaultValues: {
      inquiryType: undefined,
      name: '',
      email: '',
      organization: '',
      subject: '',
      partnerType: '',
      urgency: undefined,
      message: '',
    },
  });

  const inquiryType = watch('inquiryType');
  const message = watch('message') || '';

  // Pre-select inquiry type from URL param
  useEffect(() => {
    const type = searchParams.get('type');
    if (type && ['general', 'support', 'partner', 'feature'].includes(type)) {
      setValue('inquiryType', type as InquiryType);
    }
  }, [searchParams, setValue]);

  const onSubmit = async (data: ContactFormData) => {
    // Check honeypot and rate limiting
    const protection = validateSubmission();
    if (!protection.valid) {
      toast({
        title: protection.error || 'Submission failed',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    // Sanitize inputs before database insert
    const sanitizedData = {
      inquiry_type: data.inquiryType,
      name: sanitizeText(data.name),
      email: data.email.trim().toLowerCase(),
      organization: data.organization ? sanitizeText(data.organization) : null,
      subject: data.subject ? sanitizeText(data.subject) : null,
      partner_type: data.partnerType || null,
      urgency: data.urgency || null,
      message: sanitizeText(data.message),
      status: 'new',
    };

    const { error } = await supabase.from('contact_submissions').insert(sanitizedData);

    if (error) {
      toast({
        title: 'Error sending message',
        description: 'Please try again later.',
        variant: 'destructive',
      });
      setIsSubmitting(false);
      return;
    }

    recordSubmission();

    const inquiryLabel = inquiryOptions.find(o => o.value === data.inquiryType)?.label || 'inquiry';
    toast({
      title: 'Message sent successfully',
      description: `We've received your ${inquiryLabel.toLowerCase()} and will respond within 24-48 hours.`,
    });

    reset();
    setIsSubmitting(false);
  };

  return (
    <Layout>
      <div className="container py-12 max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-4">
            <Mail className="h-7 w-7 text-primary" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">Contact Us</h1>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Have a question, need support, or want to partner with us? We'd love to hear from you.
          </p>
        </div>

        {/* Proposal Note */}
        <Alert className="mb-6">
          <Lightbulb className="h-4 w-4" />
          <AlertDescription>
            Want to propose a new data standard?{' '}
            <Link to="/proposals" className="font-medium underline underline-offset-4 hover:text-primary">
              Visit our Proposals page
            </Link>{' '}
            to submit and vote on community standards.
          </AlertDescription>
        </Alert>

        {/* Contact Form */}
        <Card>
          <CardHeader>
            <CardTitle>Send us a message</CardTitle>
            <CardDescription>
              Fill out the form below and we'll get back to you within 24-48 hours.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Honeypot field - hidden from humans, catches bots */}
              <input type="text" {...honeypotProps} />
              
              {/* Rate limit warning */}
              {isRateLimited && (
                <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                  Too many submissions. Please wait a moment before trying again.
                </div>
              )}
              
              {/* Inquiry Type */}
              <div className="space-y-2">
                <Label htmlFor="inquiryType">What can we help you with? *</Label>
                <Select 
                  value={inquiryType} 
                  onValueChange={(v) => setValue('inquiryType', v as InquiryType, { shouldValidate: true })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select inquiry type" />
                  </SelectTrigger>
                  <SelectContent>
                    {inquiryOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {inquiryType && (
                  <p className="text-sm text-muted-foreground">
                    {inquiryOptions.find(o => o.value === inquiryType)?.description}
                  </p>
                )}
                {errors.inquiryType && (
                  <p className="text-sm text-destructive">{errors.inquiryType.message}</p>
                )}
              </div>

              {/* Common Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Your Name *</Label>
                  <Input
                    id="name"
                    placeholder="John Smith"
                    {...register('name')}
                    maxLength={100}
                  />
                  {errors.name && (
                    <p className="text-sm text-destructive">{errors.name.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    {...register('email')}
                    maxLength={255}
                  />
                  {errors.email && (
                    <p className="text-sm text-destructive">{errors.email.message}</p>
                  )}
                </div>
              </div>

              {/* Dynamic Fields Based on Inquiry Type */}
              {inquiryType === 'support' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject *</Label>
                    <Input
                      id="subject"
                      placeholder="Brief description of your issue"
                      {...register('subject')}
                      maxLength={200}
                    />
                    {errors.subject && (
                      <p className="text-sm text-destructive">{errors.subject.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="urgency">Urgency *</Label>
                    <Select 
                      value={watch('urgency')} 
                      onValueChange={(v) => setValue('urgency', v as 'low' | 'medium' | 'high', { shouldValidate: true })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select urgency level" />
                      </SelectTrigger>
                      <SelectContent>
                        {urgencyLevels.map((level) => (
                          <SelectItem key={level.value} value={level.value}>
                            {level.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.urgency && (
                      <p className="text-sm text-destructive">{errors.urgency.message}</p>
                    )}
                  </div>
                </>
              )}

              {inquiryType === 'partner' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="organization">Organization *</Label>
                    <Input
                      id="organization"
                      placeholder="Your company or organization name"
                      {...register('organization')}
                      maxLength={100}
                    />
                    {errors.organization && (
                      <p className="text-sm text-destructive">{errors.organization.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="partnerType">Partner Type *</Label>
                    <Select 
                      value={watch('partnerType')} 
                      onValueChange={(v) => setValue('partnerType', v, { shouldValidate: true })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select your organization type" />
                      </SelectTrigger>
                      <SelectContent>
                        {PARTNER_CATEGORIES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.partnerType && (
                      <p className="text-sm text-destructive">{errors.partnerType.message}</p>
                    )}
                  </div>
                </>
              )}

              {inquiryType === 'feature' && (
                <div className="space-y-2">
                  <Label htmlFor="subject">Feature Title *</Label>
                  <Input
                    id="subject"
                    placeholder="Brief title for your feature request"
                    {...register('subject')}
                    maxLength={200}
                  />
                  {errors.subject && (
                    <p className="text-sm text-destructive">{errors.subject.message}</p>
                  )}
                </div>
              )}

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message">Message *</Label>
                <Textarea
                  id="message"
                  placeholder={
                    inquiryType === 'support'
                      ? 'Describe the issue you\'re experiencing, including any error messages or steps to reproduce...'
                      : inquiryType === 'partner'
                      ? 'Tell us about your organization, how you would like to contribute, and what you hope to achieve through partnership...'
                      : inquiryType === 'feature'
                      ? 'Describe the feature you\'d like to see, how it would help you, and any implementation ideas...'
                      : 'How can we help you?'
                  }
                  rows={6}
                  {...register('message')}
                  maxLength={2000}
                />
                <div className="flex justify-between items-center">
                  {errors.message ? (
                    <p className="text-sm text-destructive">{errors.message.message}</p>
                  ) : (
                    <span />
                  )}
                  <CharacterCounter current={message.length} max={2000} min={10} />
                </div>
              </div>

              {/* Submit Button */}
              <Button 
                type="submit" 
                className="w-full" 
                disabled={!isValid || isSubmitting || isRateLimited}
              >
                {isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
